import { useEffect, useState } from "react";

import { atom, useAtom } from "jotai";

import { LOCALSTORAGE_KEY } from "@config";
import { useAppSelector } from "@hooks/redux";
import { REGION_TO_CURRENCY, USER_BIZ_COUNTRY_MAP, USER_COUNTRY_MAP_BIZ } from "@src/config/country";
import { formatCurrency, isMutipleRegion } from "@pages/pricing/utils";
import commonservice from "../apis/pricingservice";
import { useSelector } from "react-redux";
import { selectRegionName } from "@store/globalSlice";
import { getRegionIcon } from "@utils/format";
import { SELECT_COUNTRY_IMAGE } from "@store/memberCenterSlice";

interface MembershipCMSPriceItem {
  Premium_Master_Account: string;
  Standard_Master_Account: string;
  Starter_Master_Account: string;
  Professional_Master_Account: string;
  Enterprise_Master_Account: string;
  region: string;
  type: string;
  currency: string;
  Starter: string;
  Professional: string;
  Enterprise: string;
  Starter_Sub_Account: string;
  Professional_Sub_Account: string;
  Enterprise_Sub_Account: string;
}

const membershipCMSPricesAtom = atom<MembershipCMSPriceItem[]>([]);

export const useMembershipCMSPricing = () => {
  const { language, userInfo } = useAppSelector((state) => state.globalReducer);
  const [membershipCMSPrices, setMembershipCMSPrices] = useAtom(membershipCMSPricesAtom);

  const requestMembershipCMSPrices = async () => {
    const data = await commonservice.fetchMembershipPackages<{ "membership.price": MembershipCMSPriceItem[] }>();
    const membershipCMSPrices = data?.["membership.price"] || [];
    setMembershipCMSPrices(membershipCMSPrices);
  };

  const getMemberCMSPricing = (
    region: string,
    currency: string,
    duration: string,
    index: number,
    promotionDiscount?: number,
  ) => {
    const [subAccountCount] = useAtom(subAccountCountAtom);
    const [showSubAccount] = useAtom(isShowSubAccount);
    let selectCurrency = currency;
    let membershipCMSPrice;
    // @ts-ignore
    // 本地货币逻辑 由国家判断 转为根据归属地判断
    const bizCountry = userInfo?.bizCountry;
    // const bizRegion = USER_BIZ_COUNTRY_MAP[bizCountry];

    //本货币货币判断逻辑
    // const isLocalCurrency = showSubAccount
    //   ? REGION_TO_CURRENCY[bizRegion] === currency && bizRegion === region
    //   : REGION_TO_CURRENCY[region] === currency && region === region;

    const isLocalCurrency = useIsLocalCurrency();

    // const finalRegion = showSubAccount ? bizRegion : region;
    const finalRegion = region;

    let _duration = duration;
    if (duration === "YearlyPerMonthly" && showSubAccount) {
      duration = "Yearly";
    }
    if (isLocalCurrency) {
      membershipCMSPrice = membershipCMSPrices.find((item) => {
        return item.region === finalRegion && item.currency === currency && item.type === duration;
      });
    } else {
      selectCurrency = "USD";
      membershipCMSPrice = membershipCMSPrices.find((item) => {
        return item.region === finalRegion && item.currency === "USD" && item.type === duration;
      });
    }

    // console.log(membershipCMSPrice, "membershipCMSPrice");
    if (membershipCMSPrice) {
      let price;
      switch (index) {
        case 0:
          price = 0;
          break;
        case 1:
          price = showSubAccount
            ? parseFloat(membershipCMSPrice?.Starter_Master_Account) +
              parseFloat(membershipCMSPrice?.Starter_Sub_Account) * subAccountCount?.Starter_Sub_Account_Count
            : membershipCMSPrice?.Starter;
          break;
        case 2:
          price = showSubAccount
            ? parseFloat(membershipCMSPrice?.Professional_Master_Account) +
              parseFloat(membershipCMSPrice?.Professional_Sub_Account) * subAccountCount?.Standard_Sub_Account_Count
            : membershipCMSPrice?.Professional;
          break;
        case 3:
          price = showSubAccount
            ? parseFloat(membershipCMSPrice?.Enterprise_Master_Account) +
              parseFloat(membershipCMSPrice?.Enterprise_Sub_Account) * subAccountCount?.Premium_Sub_Account_Count
            : membershipCMSPrice?.Enterprise;
          break;
      }
      if (price) {
        let numericPrice = typeof price === "string" ? parseFloat(price) : price;
        if (_duration === "YearlyPerMonthly" && showSubAccount) {
          numericPrice = numericPrice / 12;
          if (selectCurrency == "USD") {
            numericPrice = parseFloat(numericPrice.toFixed(2));
          }
        }
        if (promotionDiscount) {
          const discountedPrice = parseFloat(((numericPrice * promotionDiscount) / 100).toFixed(2));
          return formatCurrency(discountedPrice, finalRegion, selectCurrency, language, isLocalCurrency);
        }
        return formatCurrency(numericPrice, finalRegion, selectCurrency, language, isLocalCurrency);
      }
    }
  };

  return {
    membershipCMSPrices,
    setMembershipCMSPrices,
    requestMembershipCMSPrices,
    getMemberCMSPricing,
  };
};

type MembershipProductObejct = any;

const membershipProductsAtom = atom<MembershipProductObejct>([]);
const selectCountryKeyAtom = atom<string>("");

export const useSelectCountryKey = () => {
  const [selectCountryKey, setSelectCountryKey] = useAtom(selectCountryKeyAtom);
  return {
    selectCountryKey,
    setSelectCountryKey,
  };
};

export const useWithMutipleRegion = () => {
  let regionName = useSelector(selectRegionName);
  const { region: pageRegion } = useAppSelector((state) => state.globalReducer);
  const { selectCountryKey } = useSelectCountryKey();
  const region = isMutipleRegion(selectCountryKey) ? selectCountryKey : pageRegion;
  const regionMutipleName = isMutipleRegion(selectCountryKey) ? selectCountryKey : regionName;
  const { regionOptions } = useAppSelector((state) => state.configReducer);
  const regionIcon = isMutipleRegion(selectCountryKey)
    ? SELECT_COUNTRY_IMAGE[selectCountryKey]
    : getRegionIcon(regionOptions, region);
  return {
    region,
    regionName: regionMutipleName,
    regionIcon,
  };
};

export const useIsLocalCurrency = () => {
  const { selectCountryKey } = useSelectCountryKey();
  const { currency, userInfo, region } = useAppSelector((state) => state.globalReducer);
  const bizCountry = userInfo?.bizCountry;
  const bizRegion = USER_BIZ_COUNTRY_MAP[bizCountry];
  // const showSubAccount = useAtom(isShowSubAccount);
  //本货币货币判断逻辑
  // const isLocalCurrency = showSubAccount
  //   ? REGION_TO_CURRENCY[bizRegion] === currency && bizRegion === region
  //   : REGION_TO_CURRENCY[region] === currency && region === region;

  const isLocalCurrency = REGION_TO_CURRENCY[bizRegion] === currency && bizRegion === region;
  if (selectCountryKey === "Global") return REGION_TO_CURRENCY["Global"] === currency;
  if (selectCountryKey === "Southeast Asia") return REGION_TO_CURRENCY["Southeast Asia"] === currency;
  if (selectCountryKey === "Ameu") return REGION_TO_CURRENCY["Ameu"].includes(currency);
  return isLocalCurrency;
};

export const useMembershipProducts = () => {
  const [membershipProducts, setMembershipProducts] = useAtom(membershipProductsAtom);
  const { setSelectCountryKey } = useSelectCountryKey();
  const requestMembershipProducts = async () => {
    // const data = await commonservice.fetchMembershipProducts<MembershipProductObejct>();
    // setMembershipProducts(data);
    const res = await commonservice.fetchV1Memberships<MembershipProductObejct>({
      source: "pc",
      dataCountry: localStorage.getItem(LOCALSTORAGE_KEY.REGION) || "US",
    });
    setMembershipProducts(res);

    const localSelectCountryKey = localStorage.getItem("localSelectCountryKey");

    const isInclude =
      res.countryArr
        .map((item: any) => USER_COUNTRY_MAP_BIZ[localSelectCountryKey || ""]?.includes?.(item.label))
        ?.some((item: any) => Boolean(item)) ||
      res.countryArr?.includes?.(USER_COUNTRY_MAP_BIZ[localSelectCountryKey || ""]);
    if (isInclude) {
      setSelectCountryKey(localSelectCountryKey || res.countryArr[0]);
    } else {
      setSelectCountryKey(res.countryArr[0]);
      localStorage.setItem("localSelectCountryKey", res.countryArr[0]);
    }
  };

  return { membershipProducts, requestMembershipProducts };
};

export const useMembershipPageStatus = () => {
  const { region } = useAppSelector((state) => state.globalReducer);
  const [status, setStatus] = useState<Status>("loading");
  const { requestMembershipCMSPrices } = useMembershipCMSPricing();
  const { requestMembershipProducts } = useMembershipProducts();

  const fetchPageData = async () => {
    try {
      setStatus("loading");
      await Promise.all([requestMembershipCMSPrices(), requestMembershipProducts()]);
      setStatus("succeeded");
    } catch (err) {
      setStatus("failed");
    }
  };

  useEffect(() => {
    fetchPageData();
  }, [region]);

  return { status, fetchPageData };
};

// 新增不同级别子账号对应的Master_Account价格
const subAccountPricingAtom = atom<{
  Starter_Sub_Account: number;
  Professional_Sub_Account: number;
  Enterprise_Sub_Account: number;
}>({
  Starter_Sub_Account: 0,
  Professional_Sub_Account: 0,
  Enterprise_Sub_Account: 0,
});

// 更新子账号的数量
export const subAccountCountAtom = atom<{
  Standard_Sub_Account_Count: number;
  Premium_Sub_Account_Count: number;
  Starter_Sub_Account_Count: number;
}>({
  Starter_Sub_Account_Count: 0,
  Premium_Sub_Account_Count: 6,
  Standard_Sub_Account_Count: 1,
});

export const isShowSubAccount = atom<boolean>(false);

// 增加子账号价格和数量的hook
export const useSubAccountPricing = () => {
  const { membershipProducts } = useMembershipProducts();
  const [subAccountPricing, setSubAccountPricing] = useAtom(subAccountPricingAtom);
  const [subAccountCount, setSubAccountCount] = useAtom(subAccountCountAtom);
  // 是否展示子账号
  const { region } = useAppSelector((state) => state.globalReducer);
  const [showSubAccount, setShowSubAccount] = useAtom(isShowSubAccount);

  useEffect(() => {
    if (membershipProducts.subAccount) {
      setShowSubAccount(true);
    } else {
      setShowSubAccount(false);
    }
  }, [region]);

  return {
    membershipProducts,
    subAccountPricing,
    setSubAccountPricing,
    subAccountCount,
    setSubAccountCount,
    showSubAccount,
  };
};
